import type { LogtoErrorCode } from '@logto/phrases';
import type { RequestErrorBody } from '@logto/schemas';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useToast from './use-toast';

/**
 * TMMIN MFA — codes the backend throws when the sign-in interaction/session is gone (expired via
 * `mfa.interactionTtlSeconds`, or never created). `session.interaction_not_found` is what
 * `experience-interaction.ts` actually throws; `session.not_found` covers the OIDC session itself.
 */
const expiredSessionCodes = new Set<string>(['session.not_found', 'session.interaction_not_found']);

export type ErrorHandlers = {
  [key in LogtoErrorCode]?: (error: RequestErrorBody) => void | Promise<void>;
} & {
  // Overwrite default global error handle logic
  global?: (error: RequestErrorBody) => void | Promise<void>;
};

const useErrorHandler = () => {
  const { t } = useTranslation();
  const { setToast } = useToast();

  const handleError = useCallback(
    async (error: unknown, errorHandlers?: ErrorHandlers) => {
      if (error instanceof HTTPError) {
        try {
          const logtoError = await error.response.json<RequestErrorBody>();

          const { code, message } = logtoError;

          const handler = errorHandlers?.[code] ?? errorHandlers?.global;

          if (handler) {
            await handler(logtoError);
          } else if (expiredSessionCodes.has(code)) {
            // Restart sign-in instead of showing a dead-end toast, so users can't keep switching
            // verification methods (WhatsApp / passkey / etc.) against a dead session.
            // eslint-disable-next-line @silverhand/fp/no-mutation
            window.location.href = '/error?message=session_expired';
          } else {
            setToast(message);
          }

          return;
        } catch (error) {
          setToast(t('error.unknown'));
          console.error(error);

          return;
        }
      }

      if (error instanceof TimeoutError) {
        setToast(t('error.timeout'));

        return;
      }

      setToast(t('error.unknown'));
      console.error(error);
    },
    [setToast, t]
  );

  return handleError;
};

export default useErrorHandler;
